import { Directive, OnInit, DoCheck, inject, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';

import { HisBaseGlobalsService } from '../../core/service/base/his-base-globals.service';
import { HisUiService } from '../../core/service/base/his-ui.service';

import { Dialog } from 'primeng/dialog';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Panel } from 'primeng/panel';
import { OverlayPanel } from 'primeng/overlaypanel';
import { AutoComplete } from 'primeng/autocomplete';
import { TTSortIcon, TreeTable, TreeTableToggler } from 'primeng/treetable';
import { Tabs, TabPanel, TabList, Tab } from 'primeng/tabs';
import { PickList } from 'primeng/picklist';
import { OrderList } from 'primeng/orderlist';
import { Table, SortIcon } from 'primeng/table';
import { Steps } from 'primeng/steps';
import { debounceTime, Subject, Subscription } from 'rxjs';

/**
 * // HIS-PATCH #266927: Barrierefreiheit title und aria-label
 */
@Directive({
    exportAs: 'p-confirmDialog, p-dialog, p-autoComplete, p-panel, p-treeTableToggler, p-tabpanel, p-tablist, p-tab, p-pickList, p-orderList, p-paginator, p-table, p-treeTable, p-steps, p-tableHeaderCheckbox, p-sortIcon, [sortMode], p-overlayPanel, [templatRight]',
    /* eslint-disable-next-line @angular-eslint/directive-selector */
    selector: 'p-confirmDialog, p-dialog, p-autoComplete, p-panel, p-treeTableToggler, p-tabpanel, p-tablist, p-tab, p-pickList, p-orderList, p-paginator, [paginator], [buttonTitle], p-table, p-treeTable, p-steps, p-tableHeaderCheckbox, p-sortIcon, p-treeTableSortIcon, [sortMode], p-overlayPanel, [templatRight]',
    standalone: false
})
export class HisButtonTitleDirective implements OnInit, DoCheck, AfterViewInit, OnDestroy  {
    private el = inject(ElementRef);

    hisBaseGlobalsService = inject(HisBaseGlobalsService);
    private hisUiService = inject(HisUiService);
    private dialog = inject(Dialog, { optional: true });
    private confirmDialog = inject(ConfirmDialog, { optional: true });
    private panel = inject(Panel, { optional: true });
    private autoComplete = inject(AutoComplete, { optional: true });
    private treeTableToggler = inject(TreeTableToggler, { optional: true });
    private tabs = inject(Tabs, { optional: true });
    private tablist = inject(TabList, { optional: true });
    private tabPanel = inject(TabPanel, { optional: true });
    private tab = inject(Tab, { optional: true });
    private pickList = inject(PickList, { optional: true });
    private orderList = inject(OrderList, { optional: true });
    private table = inject(Table, { optional: true });
    private treeTable = inject(TreeTable, { optional: true });
    private sortIcon = inject(SortIcon, { optional: true });
    private treeTableSortIcon = inject(TTSortIcon, { optional: true });
    private overlayPanel = inject(OverlayPanel, { optional: true });
    private steps = inject( Steps, { optional: true } );
    
    private elementTag: string = this.el.nativeElement.localName;

    private paginatorHandler?: PaginatorAriaTitleHandler;
    
    ngOnInit(): void {
        if(this.dialog) {
            if(this.dialog.closable){
                this.setCloseTitle(this.dialog);
            }
            if(this.dialog.maximizable){
                this.dialog.maximizeTitle = this.hisUiService.translate('image.overlay.scalable');
            }
        }
        if (this.confirmDialog) {
            this.setCloseTitle(this.confirmDialog);
        }
        if (this.panel) {
            if ((this.panel as any).header === undefined) {
                (this.panel as any).collapseText = this.hisUiService.translate('view.images.minus_circ');
                (this.panel as any).expandText = this.hisUiService.translate('view.images.plus_circ');
            }
            else {
                if (this.hisBaseGlobalsService.getLanguage() === 'de') {
                    (this.panel as any).collapseText = (this.panel as any).header
                        + ' ' + this.hisUiService.translate('collapsible.close');
                    (this.panel as any).expandText = (this.panel as any).header
                        + ' ' + this.hisUiService.translate('expand.open');
                } else {
                    (this.panel as any).collapseText = this.hisUiService.translate('view.images.minus_circ')
                        + ' ' + (this.panel as any).header;
                    (this.panel as any).expandText = this.hisUiService.translate('view.images.plus_circ')
                        + ' ' + (this.panel as any).header;
                }
            }
        }
        if (this.treeTableToggler) {
            if ((this.treeTableToggler as any).buttonTitle === undefined) {
                if (this.treeTableToggler.rowNode.node.expanded) {
                    (this.treeTableToggler as any).buttonTitle = 'Level ' + Number(this.treeTableToggler.rowNode.level + 1) + ' ' +
                        this.hisUiService.translate('collapsible.close');
                }
                else if (!this.treeTableToggler.rowNode.node.expanded) {
                    (this.treeTableToggler as any).buttonTitle = 'Level ' + Number(this.treeTableToggler.rowNode.level + 1) + ' ' +
                        this.hisUiService.translate('expand.open');
                }
            }
            else {
                let existingButtonTitleToggler = this.treeTableToggler.buttonTitle.toLowerCase();
                if (this.treeTableToggler.rowNode.node.expanded && !existingButtonTitleToggler?.includes('level')) {
                    (this.treeTableToggler as any).buttonTitle = 'Level ' + Number(this.treeTableToggler.rowNode.level + 1) + ' ' + (this.treeTableToggler as any).buttonTitle + ' ' + this.hisUiService.translate('collapsible.close');
                }
                else if (!this.treeTableToggler.rowNode.node.expanded && !existingButtonTitleToggler?.includes('level')) {
                    (this.treeTableToggler as any).buttonTitle = 'Level ' + Number(this.treeTableToggler.rowNode.level + 1) + ' ' + (this.treeTableToggler as any).buttonTitle + ' ' + this.hisUiService.translate('expand.open');
                }
            }
        }
        if (this.tablist) {
            this.tablist.nextButtonAriaLabel = this.hisUiService.translate('registertab.scrollableRight');
            this.tablist.prevButtonAriaLabel = this.hisUiService.translate('registertab.scrollableLeft');
        }
        if(this.tab){
            let text = this.el.nativeElement.textContent?.trim();
            this.tab.closeTitle = this.hisUiService.translate('registertab.closable') + ' ' + text;
        }
        if (this.pickList) {
            this.pickList.upButtonAriaLabel = this.hisUiService.translate('common.angleUp');
            this.pickList.topButtonAriaLabel = this.hisUiService.translate('common.angleDoubleUp');
            this.pickList.downButtonAriaLabel = this.hisUiService.translate('common.angleDown');
            this.pickList.bottomButtonAriaLabel = this.hisUiService.translate('common.angleDoubleDown');
            this.pickList.rightButtonAriaLabel = this.hisUiService.translate('common.angleRight');
            this.pickList.allRightButtonAriaLabel = this.hisUiService.translate('common.angleDoubleRight');
            this.pickList.leftButtonAriaLabel = this.hisUiService.translate('common.angleLeft');
            this.pickList.allLeftButtonAriaLabel = this.hisUiService.translate('common.angleDoubleLeft');
            this.pickList.selectionDescription = this.hisUiService.translate('common.picklist.selectionDescription');
            this.pickList.movingHint = this.hisUiService.translate('common.picklist.supplementaryHint');
            this.pickList.instructionForUse = this.hisUiService.translate('view.messages.information');
        }
        if (this.orderList) {
            this.orderList.upButtonAriaLabel = this.hisUiService.translate('common.angleUp');
            this.orderList.topButtonAriaLabel = this.hisUiService.translate('common.angleDoubleUp');
            this.orderList.downButtonAriaLabel = this.hisUiService.translate('common.angleDown');
            this.orderList.bottomButtonAriaLabel = this.hisUiService.translate('common.angleDoubleDown');
            this.orderList.selectionDescription = this.hisUiService.translate('common.picklist.selectionDescription');
            this.orderList.movingHint = this.hisUiService.translate('common.picklist.supplementaryHint');
            this.orderList.instructionForUse = this.hisUiService.translate('view.messages.information');
        }
        if ( this.autoComplete){
            this.autoComplete.emptyMessage = this.hisUiService.translate('common.allEntriesSelected');
            this.autoComplete.noResultsFoundMessage = this.hisUiService.translate('common.noResultsFound');
        }
        // only add buttonTitle if no p-autocomplete title is set
        if ( this.autoComplete && !this.autoComplete.el.nativeElement.title ) {
            ( this.autoComplete as any ).buttonTitle = this.hisUiService.translate( 'autoComplete.dropdown.title' );
            ( this.autoComplete as any ).appendTo = 'body';
            ( this.autoComplete as any ).removeIconLabel = this.hisUiService.translate('common.delete');
        }
        if (this.sortIcon) {
            this.sortIcon.sortableLabel = this.hisUiService.translate('table.sortableColumn');
        }
        if(this.treeTableSortIcon){
            this.treeTableSortIcon.sortableLabel = this.hisUiService.translate('table.sortableColumn');
        }

        if (this.overlayPanel) {
            this.setCloseTitle(this.overlayPanel);
        }
        if (this.steps) {
            this.steps.currentlyActive = this.hisUiService.translate('common.currently.active') + ' ' + this.hisUiService.translate('common.step') + ' ';
            this.steps.currentlyNotActive = this.hisUiService.translate('common.currently.not.active') + ' ' + this.hisUiService.translate('common.step') + ' ';
        }
    }

    ngDoCheck(): void {
        if (this.table && this.sortIcon){
            if (this.table.sortMode === 'multiple') {
                this.setMultipleSortLabel();
            }
        }
        
        if (this.panel && (this.panel as any).header === undefined) {
		    this.setPanelTitle();
        }
    }

    
    ngAfterViewInit(): void {
        if(this.table || this.treeTable){
            let dropdowns = this.el.nativeElement.querySelectorAll('p-dropdown .p-dropdown-trigger');	
            for(let dropdown of dropdowns){
                dropdown.setAttribute('aria-label', this.hisUiService.translate('autoComplete.dropdown.title'));
            }
        }
        if (this.panel && (this.panel as any).header === undefined) {
            this.setPanelTitle();
        }

        // Table and TreeTable Paginator
        if ( this.isTableElement ) {
            this.paginatorHandler = new PaginatorAriaTitleHandler(
                this.el.nativeElement,
                this.hisUiService
            );
            this.paginatorHandler.initialize();
        }
    }

    ngOnDestroy(): void {
        this.paginatorHandler?.disconnect();
    }

    setCloseTitle(component: any): void {
        component.buttonTitle = this.hisUiService.translate( 'image.alt.minus_circ' );
    }

    private get isTableElement() {
        return this.elementTag === 'p-table' || this.elementTag === 'p-treeTable';
    }

    // #285156: Barrierefreiheit der p-table bei sortMode="multiple"
    setMultipleSortLabel(): void{
        if (this.sortIcon.getBadgeValue() === 0){
            this.sortIcon.sortableLabel = this.hisUiService.translate('table.sortableColumn') + '. ' + this.hisUiService.translate('table.sortableColumn.multipleSort');
        }
        else{
            this.sortIcon.sortableLabel = '';
            this.sortIcon.sortableLabel = this.hisUiService.translate('table.sortableColumn') + '. ' + this.hisUiService.translate('table.sortableColumn.multipleSort') + '. ' + this.hisUiService.translate('table.sortableColumn.multipleSort.sortingPriority') + ' ' + this.sortIcon.getBadgeValue();
        }
    }
	
    setPanelTitle(): void {
        const nativeElement = this.el.nativeElement as HTMLElement;
        const hisHeading = nativeElement.querySelector('his-heading');
        if (hisHeading && hisHeading.textContent?.trim()) {
            const headingText = hisHeading.textContent.trim();
		    if (this.hisBaseGlobalsService.getLanguage() === 'de') {
                (this.panel as any).collapseText = headingText + ' ' + this.hisUiService.translate('collapsible.close');
                (this.panel as any).expandText = headingText + ' ' + this.hisUiService.translate('expand.open');
            } else {
                (this.panel as any).collapseText = this.hisUiService.translate('view.images.minus_circ') + ' ' + headingText;
	            (this.panel as any).expandText = this.hisUiService.translate('view.images.plus_circ') + ' ' + headingText;
		    }
        }
    }
}

/**
 * Handler class to manage aria-labels and titles for paginators within a table element.
 * 
 * Uses MutationObservers to watch for changes in paginator buttons and updates their titles accordingly.
 */
class PaginatorAriaTitleHandler {
    
    /** Observer for changes in the table element */
    private tableObserver?: MutationObserver;
    /** Map to track MutationObservers for each paginator's buttons */
    private readonly paginatorButtonsObserver?: Map<Element, MutationObserver> = new Map();
    /** Subject to trigger paginator updates */
    private readonly updatePaginator$ = new Subject<void>();
    /** Subscription to handle debounced updates */
    private readonly subscription: Subscription;
    /** Debounce delay for the update pipe. Must leave enough room for chained DOM updates
     * to be batched into one update request to the paginator, without taking too long. */
    private readonly debounceDelay = 20;

    constructor( private readonly tableElement: Element, private readonly hisUiService: HisUiService ) {
        // Use a debounce pipe to batch updates during frequent DOM changes
        this.subscription = this.updatePaginator$.pipe(
            debounceTime( this.debounceDelay )
        ).subscribe( () => {
            this.updatePaginatorActivePageTitle();
        } );
    }

    /**
     * Initialization function for the handler.
     * Sets up observers and initializes existing paginators.
     */
    public initialize(): void {
        // Initialize existing paginators
        this.initPaginatorTitles();

        // Watch for paginator additions/removals
        const tableWrapper = this.tableElement.querySelector( '.p-datatable, .p-treetable' ) as HTMLElement;
        if ( tableWrapper ) {
            this.observeTableChildren( tableWrapper );
        }
    }

    /**
     * Cleanup function for the handler.
     * Disconnect all observers created by this handler.
     */
    public disconnect(): void {
        this.subscription.unsubscribe();
        this.tableObserver?.disconnect();
        this.paginatorButtonsObserver.forEach( observer => observer.disconnect() );
        this.paginatorButtonsObserver.clear();
    }

    /**
     * Initialize titles for all paginator elements currently present.
     * @param paginatorElements Optional list of paginator elements to update. If not provided, queries the table for them.
     */
    private initPaginatorTitles( paginatorElements?: Element[] ): void {
        const _paginatorElements = paginatorElements ?? Array.from( this.tableElement.getElementsByTagName( 'p-paginator' ) );
        this.setPaginatorSelectRowsPerPageLabel( _paginatorElements );
        this.setPaginatorRowsPerPageLabel(_paginatorElements);
        _paginatorElements.forEach( paginatorElement => this.processPaginatorElement( paginatorElement ) );
    }

    /**
     * Set the aria-label for the "rows per page" select elements within the given paginator elements.
     * @param paginatorElements 
     */
    private setPaginatorSelectRowsPerPageLabel( paginatorElements: Element[] ): void {
        const paginatorSelectElements = paginatorElements.map( pe => Array.from( pe.querySelectorAll( 'p-autocomplete button.p-autocomplete-dropdown' ) ) ).flat();
        if ( paginatorSelectElements.length > 0 ) {
            const rowsPerPageLabel = this.hisUiService.translate( 'autoComplete.dropdown.title' );
            paginatorSelectElements.forEach( select => select.setAttribute( 'title', rowsPerPageLabel ) );
            paginatorSelectElements.forEach( select => select.setAttribute( 'aria-label', rowsPerPageLabel ) );
        }
    }
	
    private setPaginatorRowsPerPageLabel( paginatorElements: Element[] ): void {
	    const paginatorLabelElements = paginatorElements.map( pe => Array.from( pe.querySelectorAll( 'ul.p-paginator-pages li:last-child > .plamLabelContainer > label' ) ) ).flat();
        if (paginatorLabelElements.length > 0) {
            const rowsPerPageLabel = this.hisUiService.translate('de.his.faces.tags.table.datascroller.numRowsPerPage');
            paginatorLabelElements.forEach(label => label.textContent = rowsPerPageLabel);
        }
    }

    /**
     * Update the titles for all paginator page buttons.
     * @param paginatorPageButtons Optional list of paginator page buttons to update. If not provided, queries the table for them.
     */
    private updatePaginatorActivePageTitle( paginatorPageButtons?: HTMLButtonElement[] ) {
        const _paginatorPageButtons = paginatorPageButtons ?? this.queryPaginatorPageButtons();
        _paginatorPageButtons.forEach( btn => this.setPaginatorPageButtonLabel( btn ) );
    }

    /**
     * Query the table element for all paginator page buttons.
     * @returns Array of paginator page button elements
     */
    private queryPaginatorPageButtons() {
        return Array.from( this.tableElement.querySelectorAll<HTMLButtonElement>( 'button.p-paginator-page' ) );
    }

    /**
     * Process a paginator element to set up titles and observers.
     * @param paginatorElement The `p-paginator` element to process
     */
    private processPaginatorElement( paginatorElement: Element ): void {
        // select only actual pagination buttons; filters the "rows per page" 
        const paginatorButtons = paginatorElement.querySelectorAll<HTMLButtonElement>( 'li > button' );
        if ( !paginatorButtons || paginatorButtons.length === 0 ) {
            return;
        }

        // Labels for First/Previous Buttons
        this.setPaginatorNavigationButtonLabel( paginatorButtons[0], 'de.his.faces.tags.table.datascroller.title.first' );
        this.setPaginatorNavigationButtonLabel( paginatorButtons[1], 'de.his.faces.tags.table.datascroller.title.previous' );

        // Labels for Next/Last Buttons
        this.setPaginatorNavigationButtonLabel( paginatorButtons[paginatorButtons.length - 2], 'de.his.faces.tags.table.datascroller.title.next' );
        this.setPaginatorNavigationButtonLabel( paginatorButtons[paginatorButtons.length - 1], 'de.his.faces.tags.table.datascroller.title.last' );

        // Process page number buttons
        this.updatePaginatorActivePageTitle( Array.from( paginatorButtons ).slice( 2, -2 ) );

        // Observe paginator buttons for changes
        this.observerPaginatorButtons( paginatorElement );
    }

    /**
     * Set the aria-label and title attributes for a paginator navigation button.
     * @param button The paginator navigation button element
     * @param translationKey The translation key for the button label
     */
    private setPaginatorNavigationButtonLabel( button: HTMLButtonElement, translationKey: string ): void {
        const label = this.hisUiService.translate( translationKey );
        button.setAttribute( 'aria-label', label );
        button.setAttribute( 'title', label );
    }

    /**
     * Set the title and aria-label for a paginator page button based on its selected state.
     * @param button The paginator page button element to set the label for
     */
    private setPaginatorPageButtonLabel( button: HTMLButtonElement ): void {
        const isSelected = button.classList.contains( 'p-paginator-page-selected' ) || button.classList.contains( 'p-highlight' );
        const pageNumber = button.textContent?.trim() ?? '';

        if ( isSelected ) {
            const label = `${this.hisUiService.translate( 'common.activePage' )}: ${pageNumber}`;
            button.setAttribute( 'title', label );
            button.setAttribute( 'aria-label', label );
        } else {
            button.removeAttribute( 'title' );
            button.removeAttribute( 'aria-label' );
        }
    }

    /**
     * Observe changes to the children of the table wrapper element, specifically looking for added or removed paginator elements.
     * @param tableWrapper The table wrapper element to observe (usually `div.p-datatable` or `div.p-treetable`)
     */
    private observeTableChildren( tableWrapper: HTMLElement ) {
        this.tableObserver = new MutationObserver( ( mutations ) => {
            for ( const mutation of mutations ) {
                if ( mutation.type === 'childList' ) {
                    this.handleChildMutation( mutation );
                }
            }
        } );

        this.tableObserver.observe( tableWrapper, { childList: true } );
    }

    /**
     * Handle mutations in the children of the table wrapper element. Indicates added or removed paginator elements.
     * @param mutation The mutation record containing information about added or removed nodes
     */
    private handleChildMutation( mutation: MutationRecord ) {
        mutation.removedNodes.forEach( node => {
            if ( node instanceof HTMLElement && node.tagName.toLowerCase() === 'p-paginator' ) {
                this.handleRemovedPaginator( node );
            }
        } );

        mutation.addedNodes.forEach( node => {
            if ( node instanceof HTMLElement && node.tagName.toLowerCase() === 'p-paginator' ) {
                this.initPaginatorTitles( [node] );
            }
        } );
    }

    /**
     * Handle the removal of a paginator element by disconnecting its observer.
     * Happens when the paginator position is changed via the table config.
     * @param paginatorElement The `p-paginator` element to handle removal for
     */
    private handleRemovedPaginator( paginatorElement: Element ) {
        const observer = this.paginatorButtonsObserver.get( paginatorElement );
        if ( observer ) {
            observer.disconnect();
            this.paginatorButtonsObserver.delete( paginatorElement );
        }
    }

    /**
     * Create an observer for paginator buttons to watch for changes. Stores the observer in a map keyed by the paginator element
     * to remove/disconnect if necessary.
     * @param paginatorElement The `p-paginator` element to observe
     */
    private observerPaginatorButtons( paginatorElement: Element ) {
        if ( this.paginatorButtonsObserver.has( paginatorElement ) ) {
            return;
        }

        const buttonList = paginatorElement.querySelector<HTMLElement>( 'ul.p-paginator-pages' );
        if ( !buttonList ) {
            return;
        }

        // Whatch children for added/removed page buttons and attribute changes (e.g. class changes for selected state)
        const observer = new MutationObserver( ( mutations ) => {
            for ( const mutation of mutations ) {
                if ( mutation.type === 'childList' || mutation.type === 'attributes' ) {
                    // Notify to update paginator titles
                    this.updatePaginator$.next();
                }
            }
        } );

        observer.observe( buttonList, { childList: true, attributes: true, subtree: true, attributeFilter: ['class'] } );
        this.paginatorButtonsObserver.set( paginatorElement, observer );
    }

}
